import { z } from 'zod';
import { WEDDING_STATUSES } from '../lib/constants';
import { gmailLabelNameProblem, normalizeGmailLabelName } from '../lib/gmail-label';

/** `2027-06-12`. A calendar date with no time and no zone — see the
 *  `weddingDate` column comment in `backend/src/db/schema.ts`. Rejecting the
 *  old `…T00:00:00.000Z` form is deliberate: it is the shape that used to
 *  arrive here, and accepting it would let a client keep sending an instant. */
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a calendar date like 2027-06-12')
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'Not a real date');

/** `16:00` or `16:00:00` — the API sends the short form, Postgres returns the long one. */
const timeString = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Use a 24-hour time like 16:00');

const weddingFields = z.object({
  partner1FirstName: z.string().trim().min(1, 'Partner 1 first name is required').max(120),
  partner1LastName: z.string().trim().max(120).nullable().optional(),
  partner2FirstName: z.string().trim().min(1, 'Partner 2 first name is required').max(120),
  partner2LastName: z.string().trim().max(120).nullable().optional(),
  weddingDate: dateString.nullable().optional(),
  startTime: timeString.nullable().optional(),
  endTime: timeString.nullable().optional(),
  venueName: z.string().max(255).nullable().optional(),
  venuePlaceId: z.string().max(255).nullable().optional(),
  venueAddress: z.string().max(500).nullable().optional(),
  guestCountEstimate: z.number().int().min(0).max(100000).nullable().optional(),
  budgetEstimate: z.number().int().min(0).nullable().optional(),
  status: z.enum(WEDDING_STATUSES).default('lead'),
  contactName: z.string().max(255).nullable().optional(),
  contactEmail: z.string().email().max(255).nullable().optional(),
  contactPhone: z.string().max(50).nullable().optional(),
});

/**
 * An end time needs a start time to mean anything.
 *
 * Deliberately *not* an ordering rule. A reception running 6pm to 1am is the
 * most common evening wedding in the system, and `end > start` would reject it.
 *
 * Applied to the partial update schema too, where it checks only the fields
 * actually present — sending `endTime` alone is still wrong.
 */
const endNeedsStart = <T extends { startTime?: string | null; endTime?: string | null }>(v: T) =>
  !(v.endTime && !v.startTime);
const endNeedsStartMessage = {
  message: 'A start time is required when an end time is set',
  path: ['startTime'],
};

/**
 * The Gmail label to file this wedding's mail under, picked by the planner.
 *
 * Optional, and null means "follow the slug" rather than "no label" — that is
 * the stored shape too, so a wedding that never customized its label keeps
 * tracking the couple's names the way it always has.
 *
 * Blank normalizes to null rather than failing. The create dialog will not
 * submit an empty label, so this is for everything that is not the dialog: a
 * caller sending `''` means "no pick", and manufacturing a validation error out
 * of that would be pedantry.
 */
const gmailLabelName = z.preprocess(
  (v) => (typeof v === 'string' && normalizeGmailLabelName(v) === '' ? null : v),
  z
    .string()
    .transform(normalizeGmailLabelName)
    .superRefine((name, ctx) => {
      const problem = gmailLabelNameProblem(name);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    })
    .nullable()
    .optional(),
);

export const createWeddingSchema = weddingFields
  .extend({ gmailLabelName })
  .refine(endNeedsStart, endNeedsStartMessage);

// `slug` is absent from both: it is derived at creation and immutable once the
// first inquiry is sent, because it is the Gmail label the planner's mailbox
// already carries (PRD §F2).
//
// `gmailLabelName` is create-only for the same reason, one step earlier: the
// point of picking it is to choose where the mail lands *before* any lands
// there. Renaming a label that already has threads under it is a Gmail-side
// operation on the planner's own mailbox, not something to do behind their back.
export const updateWeddingSchema = weddingFields.partial().refine(endNeedsStart, endNeedsStartMessage);

export type CreateWeddingInput = z.infer<typeof createWeddingSchema>;
export type UpdateWeddingInput = z.infer<typeof updateWeddingSchema>;
